import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {BaseSchematizedAttribute, PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {
  applySchemaTagEditToSelected,
  getSchematizedAttributeTagFromWell,
} from 'samples/plate-maps/PlateMapUtil';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BaseEditableField from 'ui-library/BaseEditableField';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHTER, GRAY_SEMIDARK, RED_DARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import UnitAwareTagValueEditor from 'units/UnitAwareTagValueEditor';
import * as modals from 'util/Modals';
import {ElementMouseEvent} from 'util/Types';

const NEW_OPTION_SENTINEL = 'NEW_OPTION_SENTINEL';

interface NumericInput {
  numericValue: number;
  stringValue: string;
}

function NumericValueOption({
  initialValue,
  isIntegerField,
  initialIsEditing,
  isDuplicate,
  deleteOption,
  saveOption,
  assignOptionValue,
  onCancelEditing,
  unitSymbol,
  isPlannedValue,
}: {
  initialValue: {numericValue: number | null; stringValue: string};
  isIntegerField: boolean;
  initialIsEditing: boolean;
  isDuplicate: (
    existingValue: {numericValue: number | null; stringValue: string},
    newValue: {numericValue: number | null; stringValue: string}
  ) => boolean;
  deleteOption: () => void;
  saveOption: (option: NumericInput) => void;
  assignOptionValue: () => void;
  onCancelEditing?: () => void;
  unitSymbol?: string | null;
  isPlannedValue: boolean;
}): JSX.Element {
  const [isEditing, setIsEditing] = useState<boolean>(initialIsEditing);
  const [value, setValue] = useState<{numericValue: number | null; stringValue: string}>(initialValue);

  function onOptionClick(): void {
    if (!isEditing) {
      assignOptionValue();
    }
  }

  function onDeleteClick(e: ElementMouseEvent): void {
    e.stopPropagation();
    deleteOption();
  }

  function onClickCancel(): void {
    if (onCancelEditing == null) {
      setValue(initialValue);
      setIsEditing(false);
    } else {
      onCancelEditing();
    }
  }

  function onSaveChanges(): void {
    if (!isDuplicate(initialValue, value) && value.numericValue != null) {
      saveOption({numericValue: value.numericValue, stringValue: value.stringValue});
      setIsEditing(false);
    }
  }

  const editor = (
    <ControlledNumericInput
      value={value.stringValue}
      onChange={(e) => {
        setValue({numericValue: e.target.numericValue, stringValue: e.target.value});
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onSaveChanges();
        }
      }}
      validateInteger={isIntegerField}
      dataTestAttrs={{component: 'NumberFieldAssignmentSettings', element: 'input'}}
      focusOnMount={true}
    />
  );

  const displayString = isIntegerField ? value.numericValue : value.stringValue;

  return (
    <div
      className={css(styles.numericValueOption)}
      onClick={onOptionClick}
      {...getTestProps({component: 'NumberFieldAssignmentSettings', element: 'valueOption'})}
    >
      <div className={css(styles.numericOptionInner)}>
        <BaseEditableField
          dataTestAttrs={{component: 'NumberFieldAssignmentSettings', element: 'editableField'}}
          editor={
            unitSymbol != null ? (
              <UnitAwareTagValueEditor editorComponent={editor} unitSymbol={unitSymbol} />
            ) : (
              editor
            )
          }
          extraActionButtons={{
            viewMode: [
              <BenchlingIcon
                key="delete"
                className={css(styles.deleteIcon)}
                icon="delete"
                onClick={onDeleteClick}
              />,
            ],
            editMode: [],
          }}
          isBeingEdited={isEditing}
          isCompleteEnabled={!isDuplicate(initialValue, value) && value.numericValue != null}
          isWritable={!isPlannedValue}
          viewer={
            <Tooltip text="Planned value labels cannot be edited or removed" isDisabled={!isPlannedValue}>
              <div
                className={css(styles.optionName)}
                {...getTestProps({component: 'NumberFieldAssignmentSettings', element: 'optionName'})}
              >
                <span>{displayString}</span>
                {unitSymbol != null ? <span className={css(styles.unitText)}>{unitSymbol}</span> : null}
              </div>
            </Tooltip>
          }
          onCancel={onClickCancel}
          onComplete={onSaveChanges}
          onEdit={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          isFullWidth={true}
        />
      </div>
      {isDuplicate(initialValue, value) && (
        <p className={css(styles.errorMessage)}>{`Label ${displayString} already exists.`}</p>
      )}
    </div>
  );
}

export interface NumberFieldAssignmentSettingsProps {
  plateMap: PlateMap;
  onEditPlateMap: (plateMap: PlateMap) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  selectedAttribute: BaseSchematizedAttribute<SchemaField>;
}

/**
 * Settings to assign float or integer schema field values to a plate cells.
 */
export default function NumberFieldAssignmentSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
  selectedAttribute,
}: NumberFieldAssignmentSettingsProps): JSX.Element {
  const [valueMap, setValueMap] = useState<Record<string, NumericInput>>({});
  const [addingNewOption, setAddingNewOption] = useState<boolean>(false);
  const isIntegerField = useMemo(
    () => selectedAttribute.schemaField.fieldType === FieldType.INTEGER,
    [selectedAttribute.schemaField.fieldType]
  );

  useEffect(() => {
    const newValueMap = plateMap.wells
      .map((well) => getSchematizedAttributeTagFromWell(well, selectedAttribute))
      .concat(selectedAttribute.plannedValues)
      .reduce((acc: Record<string, NumericInput>, tag: MinimalSchemaTag | null) => {
        if (tag != null && tag.value != null && tag.value.length > 0) {
          acc[tag.value] = {
            numericValue: (isIntegerField ? tag.integerValue : tag.floatValue) as number,
            stringValue: tag.value,
          };
        }
        return acc;
      }, {});

    setValueMap((prev) => ({...prev, ...newValueMap}));
  }, [plateMap.wells, selectedAttribute, isIntegerField]);

  function getWellIndicesWithValue(value: NumericInput): Set<number> {
    const indices: Set<number> = new Set();
    plateMap.wells.forEach((well, index) => {
      const tagValue = getSchematizedAttributeTagFromWell(well, selectedAttribute);
      if (tagValue?.value === value.stringValue) {
        indices.add(index);
      }
    });
    return indices;
  }

  function keyFromNumericInput(value: NumericInput): string {
    // Keep exact precision for floats, but normalize ints.
    return isIntegerField ? value.numericValue.toString() : value.stringValue.trim();
  }

  async function deleteLabel(value: NumericInput): Promise<void> {
    const wellIndices = getWellIndicesWithValue(value);
    if (wellIndices.size > 0) {
      const confirmed = await modals.confirm(
        `Remove label ${value}`,
        `This label will be removed from ${wellIndices.size} wells. Are you sure you want to continue?`,
        ButtonStyle.NEGATIVE,
        {acceptText: 'Remove', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
      );
      if (!confirmed) {
        return;
      }
      const updatedPlateMap = applySchemaTagEditToSelected(
        plateMap,
        selectedAttribute,
        {value: null, integerValue: null, floatValue: null},
        wellIndices
      );
      onEditPlateMap(updatedPlateMap);
    }
    setValueMap((prev) => {
      const next = {...prev};
      delete next[keyFromNumericInput(value)];
      return next;
    });
  }

  function saveOptionChanges(oldValue: NumericInput, newValue: NumericInput): void {
    const wellIndices = getWellIndicesWithValue(oldValue);
    if (wellIndices.size > 0) {
      const updatedPlateMap = applySchemaTagEditToSelected(
        plateMap,
        selectedAttribute,
        getSchemaUpdateFromValue(newValue),
        wellIndices
      );
      onEditPlateMap(updatedPlateMap);
    }
    setValueMap((prev) => {
      const next = {...prev};
      delete next[keyFromNumericInput(oldValue)];
      next[keyFromNumericInput(newValue)] = newValue;
      return next;
    });
  }

  function getSchemaUpdateFromValue(value: NumericInput): Partial<MinimalSchemaTag> {
    // For float fields, save the exact user input in addition to the numeric value
    // so that it can be displayed with the intended decimal precision.
    if (isIntegerField) {
      return {integerValue: value.numericValue, value: value.numericValue.toString()};
    } else {
      return {floatValue: value.numericValue, value: value.stringValue.trim()};
    }
  }

  function assignValueToSelected(value: NumericInput): void {
    if (selectedWellIndices.size === 0) {
      return;
    }
    const newValue = getSchemaUpdateFromValue(value);
    const updatedPlateMap = applySchemaTagEditToSelected(
      plateMap,
      selectedAttribute,
      newValue,
      selectedWellIndices
    );
    onEditPlateMap(updatedPlateMap);
    deselectWells();
  }

  function addNewOption(value: NumericInput): void {
    setValueMap({...valueMap, [keyFromNumericInput(value)]: value});
    assignValueToSelected(value);
    setAddingNewOption(false);
  }

  function isDuplicate(
    initialValue: {numericValue: number | null; stringValue: string},
    newValue: {numericValue: number | null; stringValue: string}
  ): boolean {
    if (isIntegerField) {
      return (
        initialValue.numericValue !== newValue.numericValue &&
        newValue.numericValue != null &&
        sortedValues.map((v) => v.numericValue).includes(newValue.numericValue)
      );
    } else {
      return (
        initialValue.stringValue !== newValue.stringValue &&
        sortedValues.map((v) => v.stringValue).includes(newValue.stringValue)
      );
    }
  }

  const unitSymbol = selectedAttribute.schemaField.unitSymbol;
  const plannedValues = new Set(selectedAttribute.plannedValues.map((pv) => pv.value));
  const sortedValues = Object.values(valueMap).sort((a, b) => a.numericValue - b.numericValue);

  return (
    <div {...getTestProps({component: 'NumberFieldAssignmentSettings', element: 'root'})}>
      <p className={css(styles.helpText)}>{`Select wells in the plate, then select ${
        isIntegerField ? 'an integer' : 'a'
      } label to apply to them`}</p>
      <div className={classNames(css(styles.optionList), 'scrollbar-override')}>
        {sortedValues.map((value: NumericInput) => (
          <NumericValueOption
            key={value.stringValue}
            initialIsEditing={false}
            deleteOption={() => {
              deleteLabel(value);
            }}
            saveOption={(option) => {
              saveOptionChanges(value, option);
            }}
            assignOptionValue={() => {
              assignValueToSelected(value);
            }}
            isDuplicate={isDuplicate}
            initialValue={value}
            isIntegerField={isIntegerField}
            unitSymbol={unitSymbol}
            isPlannedValue={plannedValues.has(value.stringValue)}
          />
        ))}
        {addingNewOption ? (
          <NumericValueOption
            key={NEW_OPTION_SENTINEL}
            initialIsEditing={true}
            deleteOption={noop}
            saveOption={addNewOption}
            assignOptionValue={noop}
            onCancelEditing={() => {
              setAddingNewOption(false);
            }}
            isDuplicate={isDuplicate}
            initialValue={{numericValue: null, stringValue: ''}}
            isIntegerField={isIntegerField}
            unitSymbol={unitSymbol}
            isPlannedValue={false}
          />
        ) : (
          <Button
            buttonStyle={ButtonStyle.TEXT}
            size={ButtonSize.SMALL}
            icon="compose"
            text="Add label"
            disabled={false}
            dataTestAttrs={{component: 'NumberFieldAssignmentSettings', element: 'addButton'}}
            onClick={() => {
              setAddingNewOption(true);
            }}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
  applyButton: {
    float: 'right',
    marginTop: '16px',
  },
  optionList: {
    marginTop: '6px',
    overflowY: 'auto',
  },
  numericValueOption: {
    cursor: 'pointer',
    width: '100%',
    padding: '4px',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  numericOptionInner: {
    display: 'flex',
    alignItems: 'center',
  },

  optionName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  deleteIcon: {
    color: GRAY_DARK,
  },
  errorMessage: {
    color: RED_DARK,
    margin: '4px 0 0 16px',
  },
  unitText: {
    color: GRAY_SEMIDARK,
    paddingLeft: '2px',
  },
});
